import { FC, useEffect } from 'react';


import { useApplication } from '@eneco/common/application';
import env from '@eneco/common/env';
import logger from '@eneco/common/log';
import type { LayoutServiceExperimentInfo } from '@sitecore/types/lib';
import Script from 'next/script';

import { DataLayerEntry } from '../';
import { GTM_PLATFORM_VERSION } from './constants';
import { useGTM } from './hooks/useGTM';
import { useExperimentTracking } from './hooks/useSitecoreExperimentTracking';
import sha256Hash from './utils/encodeCustomerId';

const getEncodedCustomerId = async (customerId?: string) => {
  if (!customerId) return undefined;
  try {
    return await sha256Hash(customerId.toString());
  } catch (error) {
    logger.info('Error hashing customer ID:', error ? `${error}` : 'Unknown error');
    return undefined;
  }
};

export type Props = {
  eventsFromSSR?: DataLayerEntry[];
  experimentInfo?: LayoutServiceExperimentInfo;
  customerId?: string;
  accountId?: number | null;
  businessUnit?: string;
  label?: string;
};

export const GTM: FC<Props> = args => {
  const GTM_ID = env('GTM_ID');
  const { searchParams } = useApplication();
  const isNL = args.businessUnit === 'nl';
  if (!isNL) {
    logger.error('GTM is not supported for business unit:', `${args.businessUnit}`);
    return null;
  }
  if (!GTM_ID || searchParams.get('DisableGTM') === '1') return null;
  return <GTMInner {...args} gtmId={GTM_ID} />;
};

export const GTMInner: FC<
  Props & {
    gtmId: string;
  }
> = ({ eventsFromSSR, customerId, gtmId, experimentInfo }) => {
  const GTM_URL = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
  const { pushInitialDataLayerEvents, getIsInitialized, setIsInitialized } = useGTM();

  useEffect(() => {
    const addInitialEvents = async () => {
      const encodedCustomerId = await getEncodedCustomerId(customerId);
      if (getIsInitialized()) return;
      setIsInitialized(true);
      const initialEvents: DataLayerEntry[] = [
        {
          clientId: customerId ? `${customerId}` : undefined,
          clientIdEncoded: encodedCustomerId,
          platformVersion: GTM_PLATFORM_VERSION,
        },
        { 'gtm.start': new Date().getTime(), event: 'gtm.js' },
      ];
      eventsFromSSR?.forEach(event => initialEvents.push(event));
      pushInitialDataLayerEvents(initialEvents);
    };
    addInitialEvents();
  }, [getIsInitialized, setIsInitialized, pushInitialDataLayerEvents, customerId, eventsFromSSR]);

  useExperimentTracking(experimentInfo);

  return <Script strategy="afterInteractive" src={GTM_URL} id="gtm-script" data-testid="gtm-script" />;
};
